import { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import useFeatureFlagManagement from './hooks/useFeatureFlagManagement';
import { useNavigate, useParams } from 'react-router';
import Loading from '../../Common/components/Loading';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import { Card, CardContent } from '@/src/shadComponents/ui/card';
import { Loader2 } from 'lucide-react';
import PageShell from '@/src/Common/components/PageShell';

interface FeatureFlag {
    key: string;
    enabled: boolean;
    enabledForDevelopment: boolean;
    isPublic: boolean;
}

export default function EditFeatureFlag() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [featureFlag, setFeatureFlag] = useState<FeatureFlag | null>(null);
    const { updateFeatureFlag, getFeatureFlag, isLoading, setIsLoading } =
        useFeatureFlagManagement();

    useEffect(() => {
        let isMounted = true;

        const load = async () => {
            try {
                const featureFlag = await getFeatureFlag(id ? +id : 0);
                setFeatureFlag(featureFlag);
            } catch (e) {
                console.error('Feature flag not found');
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        load();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading) {
        return <Loading isLoading={isLoading} />;
    }

    if (!featureFlag) {
        return <div>Feature flag not found</div>;
    }

    return (
        <PageShell
            title="Edit Feature Flag"
            breadcrumbs={[
                { label: 'Admin', href: '/admin/features' },
                { label: 'Feature Flags', href: '/admin/features' },
                { label: 'Edit' },
            ]}
        >
            <Formik
                initialValues={featureFlag}
                onSubmit={async (values, actions) => {
                    const wasSuccessful = await updateFeatureFlag(values);
                    actions.setSubmitting(false);

                    if (wasSuccessful) {
                        navigate('/admin/features');
                    }
                }}
            >
                {({ isSubmitting }) => (
                    <Form
                        className="mt-6"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        <div className="space-y-12">
                            <div className="grid grid-cols-1 gap-x-8 gap-y-10  pb-12 md:grid-cols-3">
                                <div className="px-8">
                                    <h2 className="text-lg font-semibold tracking-tight text-foreground">
                                        Feature Flag Details
                                    </h2>
                                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                                        Keys follow the convention of{' '}
                                        <code>{`FEATURE_FLAG_NAME`}</code>
                                        <br />
                                        For new revisions, add{' '}
                                        <code>{`_V2`}</code> or
                                        <code>{`_V3`}</code> to the end of the
                                        key.
                                    </p>
                                </div>
                                <Card className="md:col-span-2">
                                    <CardContent className="pt-6 space-y-6">
                                        <div className="space-y-2">
                                            <Label htmlFor="key">Name</Label>
                                            <Field
                                                as={Input}
                                                id="key"
                                                name="key"
                                                placeholder="Feature Flag Key"
                                            />
                                        </div>

                                        <div className="flex items-center gap-x-2">
                                            <Field
                                                id="enabled"
                                                name="enabled"
                                                type="checkbox"
                                            />
                                            <Label htmlFor="enabled">Enabled</Label>
                                        </div>
                                        <div className="flex items-center gap-x-2">
                                            <Field
                                                id="enabledForDevelopment"
                                                name="enabledForDevelopment"
                                                type="checkbox"
                                            />
                                            <Label htmlFor="enabledForDevelopment">
                                                Development Enabled
                                            </Label>
                                        </div>
                                        <div className="flex items-center gap-x-2">
                                            <Field
                                                id="isPublic"
                                                name="isPublic"
                                                type="checkbox"
                                            />
                                            <Label htmlFor="isPublic">
                                                Public Facing
                                            </Label>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-4 pt-6">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => navigate(-1)}
                            >
                                Cancel
                            </Button>

                            <Button
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting && <Loader2 className="size-4 animate-spin" />}
                                Save
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </PageShell>
    );
}
